import React from 'react'
import { WIZARD_SCREEN_TYPES } from '../../constants'
import ServiceSelection from './ServiceSection/ServiceSelection'
import AddOnSelection from './ServiceSection/AddOnSelection'
import BodyStyle from './DetailsSection/BodyStyle'
import DemattingPolicy from './DetailsSection/DemattingPolicy'
import DescriptionEntry from './DetailsSection/DescriptionEntry'
import HairLength from './DetailsSection/HairLength'
import MattedSelection from './DetailsSection/MattedSelection'
import PhotoSubmission from './DetailsSection/PhotoSubmission'
import PreferredGroomer from './DateTimeSection/PreferredGroomer'
import DateTimeSelector from './DateTimeSection/DateTimeSelector'
import ServiceSummaryAndPayment from './ConfirmationSection/ServiceSummaryAndPayment'
import CancelationPolicy from './ConfirmationSection/CancelationPolicy'
import IntroView from '../../components/IntroView'
import CallToActionView from '../../components/CallToActionView'
import BathtubSparkle from '../../../../assets/bathtub-sparkle.png'
import { VISIT_TYPES } from '../../../../utils'

export const DEFAULT_GROOMING_APPOINTMENT_DETAILS = {
	_id: '',
	initialized: false,
	grooming_service_type: '',
	hair_length: '',
	trim_amount: '',
	notes_from_client: '',
	matted: '',
	matting_terms_accepted: false,
	cancelation_terms_accepted: false,
	preferred_staff_id: '',
	reference_photo_url: '',
	reference_photo_file: null,
	reference_photo_email_sent: false,
	add_on_services: [],
	dates: [],
	dropoff_time: '',
	month: -1,
	year: -1,
	start_time: '',
	priceMin: -1,
	priceMax: -1,
	duration: -1,
	shifts: [],
	shifts_by_staff_id: {},
	staff: [],
	staff_id: '',
	pets: [],
	specificationsUsedInPricing: {},
	isRebook: false,
	applied_promo: null,
	household_id: '',
	type: VISIT_TYPES.Grooming
}

export const GROOMING_APPOINTMENT_SECTION_IDS = {
	service: 'service',
	details: 'details',
	date: 'date',
	confirm: 'confirm'
}
export const GROOMING_APPOINTMENT_SECTIONS = {
	[GROOMING_APPOINTMENT_SECTION_IDS.service]: {
		sectionName: 'Section 1',
		sectionTitle: 'Groom Service',
		sectionDescription:
			"Let's first begin by collecting information on the type of grooming service the client wants, including enrichments."
	},
	[GROOMING_APPOINTMENT_SECTION_IDS.details]: {
		sectionName: 'Section 2',
		sectionTitle: 'Groom Details',
		sectionDescription: "Next, let's gather more details about their pet's coat length, behavior, and more."
	},
	[GROOMING_APPOINTMENT_SECTION_IDS.date]: {
		sectionName: 'Section 3',
		sectionTitle: 'Date & Time',
		sectionDescription:
			"Before checking out, let's find what date and time the client wants to book their groom for."
	},
	[GROOMING_APPOINTMENT_SECTION_IDS.confirm]: {
		sectionName: 'Section 4',
		sectionTitle: 'Confirm & Book',
		sectionDescription:
			'Review the grooming services requested, confirm payment information, and finalize the appointment.'
	}
}

export const GROOMING_APPOINTMENT_WORKFLOW = [
	{
		type: WIZARD_SCREEN_TYPES.Start,
		component: (props = {}) => (
			<CallToActionView
				mainText={`Hi ${props?.userName}, let's book a grooming appointment!`}
				secondaryText={
					props?.petName
						? `Click 'Get Started' to begin scheduling a grooming appointment for ${props?.petName}.`
						: ''
				}
				imageSrc={BathtubSparkle}
				actionTitle="Get Started"
				{...props}
			/>
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.service,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => (
			<IntroView {...GROOMING_APPOINTMENT_SECTIONS[GROOMING_APPOINTMENT_SECTION_IDS.service]} {...props} />
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.service,
		type: WIZARD_SCREEN_TYPES.Form,
		visit_attr: 'grooming_service_type',
		requires_api_update: true,
		can_proceed_condition: (appt) => appt?.grooming_service_type > '',
		component: (props = {}) => (
			<ServiceSelection title="What type of grooming service would you like? *" {...props} />
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.service,
		type: WIZARD_SCREEN_TYPES.Form,
		visit_attr: 'add_on_services',
		requires_api_update: true,
		can_proceed_condition: () => true, // optional
		component: (props = {}) => (
			<AddOnSelection
				title="Would you like to include any enrichments? (optional)"
				subtext="Please select all options that apply, according to the client's preferences."
				{...props}
			/>
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => (
			<IntroView {...GROOMING_APPOINTMENT_SECTIONS[GROOMING_APPOINTMENT_SECTION_IDS.details]} {...props} />
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		visit_attr: 'hair_length',
		requires_api_update: true,
		can_proceed_condition: (appt) => appt?.hair_length > '',
		component: (props = {}) => (
			<HairLength title={`How long is ${props?.petName}'s fur right now?*`} {...props} />
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		visit_attr: 'trim_amount',
		requires_api_update: true,
		can_proceed_condition: (appt) => appt?.trim_amount > '',
		display_step_condition: (appt) => appt?.grooming_service_type === 'haircut',
		component: (props = {}) => <BodyStyle title="What type of body style are you looking for? *" {...props} />
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		visit_attr: 'notes_from_client',
		requires_api_update: true,
		can_proceed_condition: () => true, // optional
		component: (props = {}) => (
			<DescriptionEntry
				title={`Describe what you want for ${props?.petName}'s groom. (optional)`}
				{...props}
			/>
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		visit_attr: 'reference_photo_file', // There are 3 attributes in this component, only used in index
		requires_api_update: true,
		can_proceed_condition: (appt) => {
			return true
			// if (
			// 	appt?.reference_photo_url > '' ||
			// 	(appt?.reference_photo_url === '' && appt?.reference_photo_email_sent === true) ||
			// 	(appt?.reference_photo_url === '' && appt?.reference_photo_file !== null)
			// ) {
			// 	return true
			// } else {
			// 	return false
			// }
		},
		component: (props = {}) => (
			<PhotoSubmission
				title="Share an inspiration photo."
				subtext="Photos help us know what you want your pup to look like after their groom."
				{...props}
			/>
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		visit_attr: 'matted',
		requires_api_update: true,
		can_proceed_condition: (appt) => appt?.matted > '',
		component: (props = {}) => (
			<MattedSelection title={`Is ${props?.petName}'s coat currently matted?*`} {...props} />
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		visit_attr: 'matting_terms_accepted',
		requires_api_update: false,
		can_proceed_condition: (appt) => appt?.matting_terms_accepted === true,
		component: (props = {}) => (
			<DemattingPolicy
				title="Do you agree to our dematting policy? *"
				subtext="A full terms and conditions document will be provided to the client when they check-in for their appointment."
				{...props}
			/>
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.date,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => (
			<IntroView {...GROOMING_APPOINTMENT_SECTIONS[GROOMING_APPOINTMENT_SECTION_IDS.date]} {...props} />
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.date,
		type: WIZARD_SCREEN_TYPES.Form,
		visit_attr: 'preferred_staff_id',
		requires_api_update: true,
		can_proceed_condition: (appt) => appt?.preferred_staff_id > '',
		component: (props = {}) => <PreferredGroomer title="Do you have a groomer preference?" {...props} />
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.date,
		type: WIZARD_SCREEN_TYPES.Form,
		visit_attr: 'dates',
		requires_api_update: true,
		can_proceed_condition: (appt) => {
			return appt?.dates?.length > 0 && appt?.dropoff_time > ''
		},
		component: (props = {}) => (
			<DateTimeSelector
				title={`Which date & time would you like to schedule ${props?.petName}'s groom for?`}
				{...props}
			/>
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.confirm,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => (
			<IntroView {...GROOMING_APPOINTMENT_SECTIONS[GROOMING_APPOINTMENT_SECTION_IDS.confirm]} {...props} />
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.confirm,
		type: WIZARD_SCREEN_TYPES.Form,
		visit_attr: 'cancelation_terms_accepted',
		requires_api_update: false,
		can_proceed_condition: (appt) => appt?.cancelation_terms_accepted === true,
		component: (props = {}) => (
			<CancelationPolicy
				title="Do you agree to our cancellation policy? *"
				subtext="A copy of our cancellation policy will be provided on the client's app under their appointment's details page."
				{...props}
			/>
		)
	},
	{
		section: GROOMING_APPOINTMENT_SECTION_IDS.confirm,
		type: WIZARD_SCREEN_TYPES.Invoice,
		component: (props = {}) => <ServiceSummaryAndPayment {...props} />
	},
	{
		type: WIZARD_SCREEN_TYPES.Exit,
		component: (props = {}) => (
			<CallToActionView
				mainText={`You did it! You booked a groom for ${props?.petName}.`}
				secondaryText={`Exit the wizard to view ${props?.petName}'s scheduled appointment. `}
				imageSrc={BathtubSparkle}
				actionTitle="Exit"
				{...props}
			/>
		)
	}
]
